/**
 * Ticker-driven values for the overlays and the markers (owner B5).
 *
 *   const anim = new Animator();
 *   const veil = anim.value(0, (v) => map.setPaintProperty("ld-veil", "background-opacity", v));
 *   await veil.to(1, 1100, EASE.drift);           // resolves true when reached, false when superseded
 *
 *   const fs = new FeatureFx(map, "ld-places-cluster", anim);
 *   fs.to("phoenix-marketcity", "h", 1, 240, EASE.settle);   // feature-state tween, flushed once per frame
 *
 * Engine rule: nothing here goes through React. One ticker subscription per Animator, alive only while
 * something moves. Every write is guarded, so a map that is being removed never throws from a frame.
 */
import type { Map as MLMap } from "maplibre-gl";
import { ticker } from "@/lib/ticker";

export type EaseFn = (t: number) => number;

interface Run {
  from: number;
  to: number;
  t0: number;
  dur: number;
  ease: EaseFn;
  resolve: (ok: boolean) => void;
}

/** One animated number. `write` is called with every new value (never twice with the same one). */
export class Tween {
  v: number;
  private run: Run | null = null;
  private readonly write: (v: number) => void;
  private readonly anim: Animator;

  constructor(v: number, write: (v: number) => void, anim: Animator) {
    this.v = v;
    this.write = write;
    this.anim = anim;
  }

  /** Where the value is heading (its current value when idle). */
  get target(): number {
    return this.run ? this.run.to : this.v;
  }

  get active(): boolean {
    return this.run !== null;
  }

  /** Jumps to v. A running tween is cancelled (its promise resolves false). */
  set(v: number): void {
    this.cancel();
    if (v !== this.v) {
      this.v = v;
      this.write(v);
    }
  }

  /** Re-sends the current value (after a layer was re-created, or a mood changed a factor). */
  rewrite(): void {
    this.write(this.v);
  }

  /** Tweens to v over `dur` ms after `delay` ms. Resolves true when it arrives, false if superseded. */
  to(v: number, dur: number, ease: EaseFn, delay = 0): Promise<boolean> {
    this.cancel();
    if (dur <= 0 && delay <= 0) {
      this.set(v);
      return Promise.resolve(true);
    }
    if (v === this.v && delay <= 0) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
      this.run = { from: this.v, to: v, t0: performance.now() + Math.max(0, delay), dur: Math.max(1, dur), ease, resolve };
      this.anim.kick(this);
    });
  }

  cancel(): void {
    const r = this.run;
    this.run = null;
    r?.resolve(false);
  }

  /** Advances to `now`; true while still running. */
  step(now: number): boolean {
    const r = this.run;
    if (!r) return false;
    if (now < r.t0) return true;
    // A delayed tween starts from wherever the value is when its delay ends.
    const t = Math.min(1, (now - r.t0) / r.dur);
    const v = t >= 1 ? r.to : r.from + (r.to - r.from) * r.ease(t);
    if (v !== this.v) {
      this.v = v;
      this.write(v);
    }
    if (t >= 1) {
      this.run = null;
      r.resolve(true);
      return false;
    }
    return true;
  }
}

/** Owns one ticker subscription for any number of tweens, plus per-frame flush hooks. */
export class Animator {
  private readonly running = new Set<Tween>();
  private readonly flushers = new Set<() => void>();
  private off: (() => void) | null = null;
  private dead = false;

  value(v0: number, write: (v: number) => void): Tween {
    return new Tween(v0, write, this);
  }

  /** Called after every frame's tweens have stepped (e.g. to batch feature-state writes). */
  onFlush(fn: () => void): () => void {
    this.flushers.add(fn);
    return () => this.flushers.delete(fn);
  }

  kick(t: Tween): void {
    if (this.dead) return;
    this.running.add(t);
    if (!this.off) this.off = ticker.add(this.frame);
  }

  get busy(): boolean {
    return this.running.size > 0;
  }

  private readonly frame = (now: number): boolean => {
    if (this.dead) {
      this.off = null;
      return false;
    }
    for (const t of [...this.running]) if (!t.step(now)) this.running.delete(t);
    for (const f of [...this.flushers]) f();
    if (this.running.size === 0) {
      this.off = null;
      return false;
    }
    return true;
  };

  destroy(): void {
    if (this.dead) return;
    this.dead = true;
    for (const t of [...this.running]) t.cancel();
    this.running.clear();
    this.flushers.clear();
    this.off?.();
    this.off = null;
  }
}

export type FeatureId = string | number;

/**
 * Feature-state values per feature and key, tweened on the ticker and written to MapLibre once per frame
 * (one setFeatureState per changed feature). Feature-state changes never transition by themselves, so
 * every visible state change in the markers and corridors goes through here.
 */
export class FeatureFx {
  private readonly vals = new Map<FeatureId, Map<string, Tween>>();
  private readonly dirty = new Map<FeatureId, Record<string, number>>();
  private scheduled = false;
  private dead = false;
  private readonly offFlush: () => void;
  private readonly map: MLMap;
  private readonly source: string;
  private readonly anim: Animator;
  private readonly defaults: Record<string, number>;

  constructor(map: MLMap, source: string, anim: Animator, defaults: Record<string, number> = {}) {
    this.map = map;
    this.source = source;
    this.anim = anim;
    this.defaults = defaults;
    this.offFlush = anim.onFlush(() => this.flush());
  }

  private tween(id: FeatureId, key: string): Tween {
    let m = this.vals.get(id);
    if (!m) {
      m = new Map();
      this.vals.set(id, m);
    }
    let t = m.get(key);
    if (!t) {
      t = this.anim.value(this.defaults[key] ?? 0, (v) => this.mark(id, key, v));
      m.set(key, t);
    }
    return t;
  }

  private mark(id: FeatureId, key: string, v: number): void {
    let d = this.dirty.get(id);
    if (!d) {
      d = {};
      this.dirty.set(id, d);
    }
    d[key] = Math.round(v * 10000) / 10000;
    // Jumps outside a frame still reach the map this turn.
    if (!this.anim.busy && !this.scheduled) {
      this.scheduled = true;
      queueMicrotask(() => {
        this.scheduled = false;
        this.flush();
      });
    }
  }

  get(id: FeatureId, key: string): number {
    return this.vals.get(id)?.get(key)?.v ?? this.defaults[key] ?? 0;
  }

  target(id: FeatureId, key: string): number {
    return this.vals.get(id)?.get(key)?.target ?? this.defaults[key] ?? 0;
  }

  set(id: FeatureId, key: string, v: number): void {
    const t = this.tween(id, key);
    t.set(v);
    // A value equal to the current one still has to reach a freshly loaded source once.
    if (!this.dirty.has(id)) this.mark(id, key, t.v);
  }

  to(id: FeatureId, key: string, v: number, dur: number, ease: EaseFn, delay = 0): Promise<boolean> {
    const t = this.tween(id, key);
    if (t.target === v && !t.active) return Promise.resolve(true);
    return t.to(v, dur, ease, delay);
  }

  /** Re-sends every known value (after the source's data changed). */
  rewriteAll(): void {
    for (const [id, m] of this.vals) for (const [key, t] of m) this.mark(id, key, t.v);
  }

  /** Forgets features that are no longer in the data. */
  retain(ids: Set<FeatureId>): void {
    for (const id of [...this.vals.keys()]) {
      if (ids.has(id)) continue;
      for (const t of this.vals.get(id)!.values()) t.cancel();
      this.vals.delete(id);
      this.dirty.delete(id);
    }
  }

  flush(): void {
    if (this.dead || this.dirty.size === 0) return;
    const batch = [...this.dirty];
    this.dirty.clear();
    try {
      if (!this.map.getSource(this.source)) return;
      for (const [id, s] of batch) this.map.setFeatureState({ source: this.source, id }, s);
    } catch {
      /* the map is being removed */
    }
  }

  destroy(): void {
    if (this.dead) return;
    this.dead = true;
    this.offFlush();
    for (const m of this.vals.values()) for (const t of m.values()) t.cancel();
    this.vals.clear();
    this.dirty.clear();
  }
}
